import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Tiny admin panel for Paul. Access via /?admin=<key>.
 * Lists pending reviews with Approve / Reject / Delete controls.
 */
export default function AdminReviews({ adminKey }) {
    const [tab, setTab] = useState("pending");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await axios.get(`${API}/admin/reviews`, {
                params: { key: adminKey, status: tab },
            });
            setItems(res.data);
        } catch (e) {
            setError(
                e?.response?.status === 401
                    ? "Invalid admin key."
                    : "Couldn't load reviews."
            );
        } finally {
            setLoading(false);
        }
    }, [adminKey, tab]);

    useEffect(() => {
        load();
    }, [load]);

    const moderate = async (id, status) => {
        try {
            await axios.patch(
                `${API}/admin/reviews/${id}`,
                { status },
                { params: { key: adminKey } }
            );
            setItems((prev) => prev.filter((r) => r.id !== id));
        } catch {
            setError("Update failed.");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this review permanently?")) return;
        try {
            await axios.delete(`${API}/admin/reviews/${id}`, {
                params: { key: adminKey },
            });
            setItems((prev) => prev.filter((r) => r.id !== id));
        } catch {
            setError("Delete failed.");
        }
    };

    const exit = () => {
        window.location.href = window.location.origin;
    };

    return (
        <div
            data-testid="admin-panel"
            className="min-h-screen bg-black text-white p-6 md:p-10 font-mono"
        >
            <div className="flex items-center justify-between mb-8 border-b border-white/15 pb-4">
                <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#FFE600]">
                        PaintKing · Admin
                    </div>
                    <div className="font-display uppercase text-3xl md:text-4xl mt-1">
                        Review Moderation
                    </div>
                </div>
                <button
                    data-testid="admin-exit-btn"
                    onClick={exit}
                    className="text-[10px] tracking-[0.3em] uppercase border border-white/30 hover:border-[#FFE600] hover:text-[#FFE600] px-4 py-2 transition-colors"
                >
                    ← Exit
                </button>
            </div>

            <div className="flex gap-2 mb-6">
                {["pending", "approved", "rejected"].map((t) => (
                    <button
                        key={t}
                        data-testid={`admin-tab-${t}`}
                        onClick={() => setTab(t)}
                        className={`text-[10px] tracking-[0.3em] uppercase px-4 py-2 border transition-colors ${
                            tab === t
                                ? "border-[#FFE600] text-[#FFE600] bg-[#FFE600]/[0.06]"
                                : "border-white/20 text-white/60 hover:text-white"
                        }`}
                    >
                        {t}
                    </button>
                ))}
            </div>

            {error && (
                <div className="mb-6 border border-red-500/50 bg-red-500/10 text-red-300 text-[13px] px-4 py-3">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="text-white/50 text-[12px] tracking-[0.28em] uppercase">
                    Loading…
                </div>
            ) : items.length === 0 ? (
                <div
                    data-testid="admin-empty"
                    className="border border-white/10 p-10 text-center text-white/50 text-[12px] tracking-[0.28em] uppercase"
                >
                    No {tab} reviews
                </div>
            ) : (
                <div className="space-y-4">
                    {items.map((r) => (
                        <div
                            key={r.id}
                            data-testid={`admin-review-${r.id}`}
                            className="border border-white/15 bg-white/[0.02] p-6"
                        >
                            <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="flex gap-0.5">
                                        {Array.from({ length: r.rating }).map((_, s) => (
                                            <svg
                                                key={s}
                                                viewBox="0 0 24 24"
                                                className="w-4 h-4 fill-[#FFE600]"
                                            >
                                                <path d="M12 2l2.9 6.9L22 10l-5.5 4.8L18 22l-6-3.6L6 22l1.5-7.2L2 10l7.1-1.1L12 2z" />
                                            </svg>
                                        ))}
                                    </div>
                                    <div className="font-display uppercase text-lg">
                                        {r.name}
                                    </div>
                                    <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">
                                        {r.city}
                                    </div>
                                </div>
                                <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">
                                    {new Date(r.created_at).toLocaleString()}
                                </div>
                            </div>
                            <p className="text-[13px] leading-relaxed text-white/85 mb-4">
                                “{r.quote}”
                            </p>
                            <div className="flex gap-2 flex-wrap">
                                {tab !== "approved" && (
                                    <button
                                        data-testid={`admin-approve-${r.id}`}
                                        onClick={() => moderate(r.id, "approved")}
                                        className="text-[10px] tracking-[0.3em] uppercase border border-[#FFE600] text-[#FFE600] hover:bg-[#FFE600] hover:text-black px-4 py-2 transition-colors"
                                    >
                                        Approve
                                    </button>
                                )}
                                {tab !== "rejected" && (
                                    <button
                                        data-testid={`admin-reject-${r.id}`}
                                        onClick={() => moderate(r.id, "rejected")}
                                        className="text-[10px] tracking-[0.3em] uppercase border border-white/40 hover:border-white text-white/70 hover:text-white px-4 py-2 transition-colors"
                                    >
                                        Reject
                                    </button>
                                )}
                                <button
                                    data-testid={`admin-delete-${r.id}`}
                                    onClick={() => remove(r.id)}
                                    className="text-[10px] tracking-[0.3em] uppercase border border-red-500/40 text-red-400 hover:bg-red-500/10 px-4 py-2 transition-colors"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
